import { useCallback, useEffect, useState } from 'react'
import type { DocContent, DocMeta, DocVersion, Member } from '@cs/shared'
import { api } from '../api.js'
import { describeError } from '../App.js'

/**
 * Save a document to disk.
 *
 * Built in the browser rather than linked from the hub: the text is already here,
 * and a download route would need its own auth path for something that is only ever
 * the content on screen. The filename is the document's own path, flattened —
 * `spec/orders.md` becomes `spec_orders.md` rather than silently losing the folder.
 */
function download(path: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/markdown;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = path.replace(/\//g, '_')
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoked on the next tick: Safari needs the object to outlive the click.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function Specs({
  room,
  me,
  onChanged,
}: {
  room: string
  me: Member
  onChanged: () => void
}) {
  const [docs, setDocs] = useState<DocMeta[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [doc, setDoc] = useState<DocContent | null>(null)
  const [versions, setVersions] = useState<DocVersion[]>([])
  const [patch, setPatch] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [draftText, setDraftText] = useState('')
  const [note, setNote] = useState('')
  const [newPath, setNewPath] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = me.role === 'owner' || me.role === 'member'

  const loadDocs = useCallback(async () => {
    try {
      const result = await api.docs(room)
      setDocs(result.docs)
      setSelected((prev) => prev ?? result.docs[0]?.path ?? null)
    } catch (err) {
      setError(describeError(err))
    }
  }, [room])

  useEffect(() => {
    void loadDocs()
  }, [loadDocs])

  useEffect(() => {
    if (!selected) {
      setDoc(null)
      return
    }
    setPatch(null)
    setEditing(false)
    Promise.all([api.doc(room, selected), api.docVersions(room, selected)])
      .then(([content, history]) => {
        setDoc(content)
        setVersions(history.versions)
        setDraftText(content.content)
      })
      .catch((err) => setError(describeError(err)))
  }, [room, selected])

  const showDiff = async (versionId: string) => {
    if (!selected) return
    try {
      const result = await api.docDiff(room, selected, undefined, versionId)
      setPatch(result.patch)
    } catch (err) {
      setError(describeError(err))
    }
  }

  const propose = async (path: string, content: string, baseVersion: string | null) => {
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await api.proposeDoc(room, path, { content, note, base_version: baseVersion })
      // Deliberately not applied here: a proposal becomes a version only when its
      // author approves it, which is the same gate everything else goes through.
      setInfo('提案を作成しました。「共有待ち」タブで確認して共有すると確定します。')
      setEditing(false)
      setNote('')
      setNewPath('')
      onChanged()
      await loadDocs()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {error && <div className="error">{error}</div>}
      {info && <div className="notice">{info}</div>}

      <div className="split">
        <div className="card">
          <h3>テキスト（版管理あり）</h3>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            md や txt。履歴が残り、版の差分を見られます。
          </p>
          <div className="doc-list">
            {docs.length === 0 && <span className="muted">まだありません</span>}
            {docs.map((d) => (
              <button
                key={d.path}
                aria-current={selected === d.path}
                onClick={() => setSelected(d.path)}
              >
                {d.path}
                {d.proposed_count > 0 && <span className="badge">{d.proposed_count}</span>}
              </button>
            ))}
          </div>

          {canEdit && (
            <>
              <h3 style={{ marginTop: 16 }}>新規作成</h3>
              <input
                value={newPath}
                onChange={(e) => setNewPath(e.target.value)}
                placeholder="spec/orders.md"
              />
              <div className="row" style={{ marginTop: 8 }}>
                <button
                  disabled={busy || !newPath.trim()}
                  onClick={() => {
                    setSelected(null)
                    setDoc(null)
                    setDraftText(`# ${newPath.replace(/\.md$/, '')}\n\n`)
                    setEditing(true)
                  }}
                >
                  下書きを開く
                </button>
              </div>
            </>
          )}
        </div>

        <div className="card">
          {editing ? (
            <>
              <h2>{selected ?? newPath}</h2>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                style={{ minHeight: 380, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              />
              <div style={{ height: 8 }} />
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="変更メモ (任意)"
              />
              <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditing(false)}>やめる</button>
                <button
                  className="primary"
                  disabled={busy}
                  onClick={() =>
                    propose(selected ?? newPath.trim(), draftText, doc?.version_id ?? null)
                  }
                >
                  変更を提案
                </button>
              </div>
            </>
          ) : doc ? (
            <>
              <div className="row">
                <h2 style={{ margin: 0 }}>{doc.path}</h2>
                <span className="muted">
                  {doc.updated_by_name} · {new Date(doc.updated_at).toLocaleString('ja-JP')}
                </span>
                <button
                  style={{ marginLeft: 'auto' }}
                  onClick={() => download(doc.path, doc.content)}
                >
                  ダウンロード
                </button>
                {canEdit && (
                  <button
                    onClick={() => {
                      setDraftText(doc.content)
                      setEditing(true)
                    }}
                  >
                    編集して提案
                  </button>
                )}
              </div>
              <pre style={{ marginTop: 12 }}>{doc.content}</pre>

              <h3 style={{ marginTop: 16 }}>版履歴</h3>
              {versions.map((v) => (
                <div key={v.version_id} className="row" style={{ marginBottom: 4 }}>
                  <span className="muted">
                    {new Date(v.created_at).toLocaleString('ja-JP')} · {v.author_name}
                    {v.note ? ` — ${v.note}` : ''}
                  </span>
                  <button onClick={() => showDiff(v.version_id)}>差分</button>
                </div>
              ))}

              {patch && <Diff patch={patch} />}
            </>
          ) : (
            <p className="muted">左からドキュメントを選んでください。</p>
          )}
        </div>
      </div>
    </>
  )
}

/** Minimal unified-diff colouring — enough to review a spec change at a glance. */
function Diff({ patch }: { patch: string }) {
  return (
    <pre className="diff" style={{ marginTop: 12 }}>
      {patch.split('\n').map((line, i) => {
        const cls = line.startsWith('+')
          ? 'add'
          : line.startsWith('-')
            ? 'del'
            : line.startsWith('@@')
              ? 'hunk'
              : undefined
        return (
          <div key={i} className={cls}>
            {line || ' '}
          </div>
        )
      })}
    </pre>
  )
}
